import { createColumnHelper } from "@tanstack/react-table";
import { ManufacturingSchema } from "api/manufacturingNew/models";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { cx, dateFns } from "utilities";
import { StateLabel } from "components/common/stateLabel";
import styles from "./ManufacturingSchemasList.module.css";
import { AvatarOrInitials } from "components/utils/userAvatar/UserAvatar";
import { MoreDataCounter } from "components/common/moreDataCounter";
import {
  attributeManager,
  AttributeObject,
} from "pages/callCenter/shared/attributesList/AttributesList";
import { useMemo } from "react";

const columnHelper = createColumnHelper<ManufacturingSchema>();

export const useManufacturingSchemasListColumns = () => {
  return useMemo(() => {
    return [
      columnHelper.accessor(row => row.signature, {
        id: "signature",
        header: "sygnatura",
        size: 130,
        cell: info => (
          <Typography fontSize="14" fontWeight="600">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor(row => row.name, {
        header: "nazwa",
        size: 200,
        cell: info => (
          <Typography fontSize="12" fontWeight="700">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor(row => row.description, {
        header: "opis",
        size: 200,
        cell: info => (
          <Typography fontSize="12" fontWeight="700">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor(row => row.createdBy, {
        header: "tworzony przez",
        size: 200,
        cell: info => {
          const user = info.getValue();
          if (!user) return <div />;
          return (
            <div className={cx("d-flex align-items-center gap-2")}>
              <AvatarOrInitials
                avatarSrc={user.avatar}
                firstName={user.firstName}
                lastName={user.lastName}
                className={styles.avatar}
              />

              <Typography fontSize="12" fontWeight="700">
                {user.firstName} {user.lastName}
              </Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.stages, {
        header: "liczba etapów",
        size: 80,
        cell: info => (
          <Typography fontSize="12" fontWeight="700">
            {info.getValue()?.length}
          </Typography>
        ),
      }),
      columnHelper.accessor(row => row.stages, {
        header: "etapy",
        size: 350,
        cell: info => {
          let stagesShown: StageForManager[] = [];
          let stagesWrapped: StageForManager[] = [];
          const stages = info.getValue();

          if (stages) {
            const stageAccumulator: StageForManager[] = [];

            stages.forEach(stage => {
              stageAccumulator.push({
                attribute: stage.name,
                value: "",
                color: stage.textColor,
                backgroundColor: stage.backgroundColor,
              });
            });
            [stagesShown, stagesWrapped] = attributeManager(stageAccumulator, 44);
          }
          return (
            <div className="overflow position-relative d-flex align-items-center py-2 pr-2 gap-1">
              {stagesShown.map((stage, index) => {
                return (
                  <StateLabel
                    key={stage.value + index}
                    kind="none"
                    style={{
                      backgroundColor: stage.backgroundColor,
                      color: stage.textColor,
                    }}
                    className="overflow uppercase fw-700 "
                  >
                    {stage.attribute}
                  </StateLabel>
                );
              })}
              {stagesWrapped.length > 0 ? (
                <MoreDataCounter className="ml-2" counter={stagesWrapped.length} />
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.lastUpdatedAt, {
        header: "ostatnia edycja",
        cell: info => (
          <Typography fontSize="12" fontWeight="700">
            {info.getValue() ? dateFns.format(new Date(info.getValue()), "dd.MM.yyyy") : ""}
          </Typography>
        ),
      }),
    ];
  }, []);
};

interface StageForManager extends AttributeObject {
  textColor?: string;
  backgroundColor?: string;
}
